// src/pages/GuestBookingPage.jsx
import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { doc, getDoc, runTransaction, serverTimestamp } from "firebase/firestore";
import { db } from "../config/firebase";
import { useTenant } from "../context/TenantContext";
import { useAuth } from "../context/AuthContext";
import { formatDate } from "../utils/dateUtils";
import { slotDocId, occupiedSlots } from "../utils/slotUtils";
import RescheduleModal from "../components/booking/RescheduleModal";
import { LoadingSpinner } from "../components/ui";
import {
  CheckCircle, XCircle, Scissors, Calendar, Clock,
  AlertCircle, Loader2, X, CalendarCheck, UserPlus,
} from "lucide-react";

const CANCEL_REASONS = [
  "Schedule conflict",
  "Found another option",
  "Feeling unwell",
  "Personal emergency",
  "Other",
];

const DEFAULT_CANCEL_POLICY_HOURS = 24;

export default function GuestBookingPage() {
  const { bookingId } = useParams();
  const { tenant }    = useTenant();
  const { user }      = useAuth();
  const navigate      = useNavigate();

  const [booking,        setBooking]        = useState(null);
  const [loading,        setLoading]        = useState(true);
  const [notFound,       setNotFound]       = useState(false);
  const [confirming,     setConfirming]     = useState(false);
  const [cancelling,     setCancelling]     = useState(false);
  const [reason,         setReason]         = useState("");
  const [error,          setError]          = useState("");
  const [rescheduleOpen, setRescheduleOpen] = useState(false);
  const [successMsg,     setSuccessMsg]     = useState("");

  useEffect(() => {
    async function load() {
      try {
        const snap = await getDoc(doc(db, "bookings", bookingId));
        if (!snap.exists()) { setNotFound(true); return; }
        setBooking({ id: snap.id, ...snap.data() });
      } catch (err) {
        console.error(err);
        setError("Could not load booking. Please try again.");
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [bookingId]);

  async function handleCancel() {
    setCancelling(true);
    setError("");
    try {
      const duration = booking.service?.duration || 30;
      const windows  = occupiedSlots(booking.time, duration);
      await runTransaction(db, async (tx) => {
        tx.update(doc(db, "bookings", bookingId), {
          status:       "cancelled",
          cancelledAt:  serverTimestamp(),
          cancelReason: reason || null,
          cancelledBy:  "customer",
        });
        for (const t of windows) {
          const ref = doc(db, "slots", slotDocId(booking.tenantId, booking.barberId, booking.dateStr, t));
          tx.set(ref, { status: "cancelled" }, { merge: true });
        }
      });
      setBooking(b => ({ ...b, status: "cancelled" }));
      setConfirming(false);
      setReason("");
      setSuccessMsg("Your appointment has been cancelled. A confirmation has been sent to your email.");
    } catch (err) {
      console.error(err);
      setError("Failed to cancel. Please try again.");
    } finally {
      setCancelling(false);
    }
  }

  function handleRescheduleSuccess(updated) {
    setBooking(b => ({ ...b, ...updated }));
    setRescheduleOpen(false);
    const when = updated.dateStr ? formatDate(new Date(updated.date)) : "";
    setSuccessMsg(`Your appointment has been rescheduled to ${when}${updated.time ? ` at ${updated.time}` : ""}.`);
  }

  const slug = tenant?.slug || tenant?.id;

  if (loading) {
    return (
      <div className="min-h-screen bg-surface-50 flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (notFound || (!loading && !booking && !error)) {
    return (
      <div className="min-h-screen bg-surface-50 flex items-center justify-center px-4">
        <div className="text-center">
          <div className="text-5xl mb-4">🔍</div>
          <h2 className="font-semibold text-2xl text-ink-900 mb-2">Booking not found</h2>
          <p className="text-ink-400 text-sm mb-6">
            This booking link may be invalid or the appointment was removed.
          </p>
          <button onClick={() => navigate(`/${slug}/book`)} className="btn-primary">
            Make a New Booking →
          </button>
        </div>
      </div>
    );
  }

  if (error && !booking) {
    return (
      <div className="min-h-screen bg-surface-50 flex items-center justify-center px-4">
        <div className="card p-8 text-center max-w-sm">
          <AlertCircle size={32} className="text-red-400 mx-auto mb-3" />
          <p className="text-ink-600 text-sm">{error}</p>
          <button onClick={() => window.location.reload()} className="btn-secondary mt-4 text-sm">
            Try again
          </button>
        </div>
      </div>
    );
  }

  const cancelPolicyHours = tenant?.cancelPolicyHours ?? DEFAULT_CANCEL_POLICY_HOURS;
  const date        = booking.date ? new Date(booking.date) : null;
  const hoursUntil  = date ? (date - new Date()) / 36e5 : -1;
  const isUpcoming  = hoursUntil > 0 && booking.status !== "cancelled";
  const isCancelled = booking.status === "cancelled";
  const tooLate     = isUpcoming && hoursUntil < cancelPolicyHours;
  const canManage   = isUpcoming && !tooLate;

  return (
    <>
      <div className="min-h-screen bg-surface-50">
        <div className="max-w-lg mx-auto px-4 py-10">

          {/* Header */}
          <div className="mb-6 text-center">
            <h1 className="font-semibold text-3xl text-ink-900">Your Booking</h1>
            <p className="text-ink-400 text-sm mt-1 font-sans">{tenant?.name}</p>
          </div>

          {/* Success banner */}
          {successMsg && (
            <div className="mb-6 flex items-start gap-2.5 px-4 py-3 rounded-xl bg-green-50
                            border border-green-200 text-green-700 text-sm font-sans animate-fade-in">
              <CheckCircle size={16} className="shrink-0 mt-0.5" />
              <span>{successMsg}</span>
              <button onClick={() => setSuccessMsg("")}
                className="ml-auto text-green-400 hover:text-green-700 transition-colors shrink-0">
                <X size={15} />
              </button>
            </div>
          )}

          {/* Status badge */}
          <div className="flex justify-center mb-6">
            {isCancelled ? (
              <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-red-50
                              border border-red-200 text-red-600 text-xs font-medium">
                <XCircle size={13} /> Cancelled
              </div>
            ) : isUpcoming ? (
              <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-brand-50
                              border border-brand-200 text-brand-700 text-xs font-medium">
                <CheckCircle size={13} /> Confirmed
              </div>
            ) : (
              <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-surface-100
                              border border-surface-200 text-ink-500 text-xs font-medium">
                <CheckCircle size={13} /> Completed
              </div>
            )}
          </div>

          {/* Booking card */}
          <div className="card p-6 mb-4">
            <div className="flex items-center gap-3 mb-5 pb-4 border-b border-surface-100">
              <div className="w-11 h-11 rounded-xl bg-brand-50 flex items-center justify-center">
                <Scissors size={18} className="text-brand-500" />
              </div>
              <div>
                <div className="font-semibold text-ink-900 text-lg leading-tight">
                  {booking.service?.name}
                </div>
                <div className="text-ink-400 text-xs font-mono mt-0.5">
                  {booking.service?.duration} min · ${booking.service?.price}
                </div>
              </div>
            </div>

            <div className="space-y-3">
              {[
                { icon: Scissors,  label: "Barber", value: booking.barber?.name },
                { icon: Calendar,  label: "Date",   value: date ? formatDate(date) : "—" },
                { icon: Clock,     label: "Time",   value: booking.time },
              ].map(({ icon: Icon, label, value }) => (
                <div key={label} className="flex items-center justify-between text-sm">
                  <span className="flex items-center gap-2 text-ink-400 font-sans">
                    <Icon size={13} className="text-brand-400" /> {label}
                  </span>
                  <span className="text-ink-700 font-medium font-mono">{value}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Error */}
          {error && (
            <div className="mb-4 flex items-center gap-2 text-sm text-red-600 bg-red-50
                            border border-red-200 rounded-xl px-4 py-3">
              <AlertCircle size={15} className="shrink-0" /> {error}
            </div>
          )}

          {/* Cancel confirmation flow */}
          {confirming && (
            <div className="card p-4 mb-4 border border-red-200 bg-red-50/40 space-y-3">
              <p className="text-sm font-medium text-red-600">Cancel this appointment?</p>
              <select
                value={reason}
                onChange={e => setReason(e.target.value)}
                className="w-full text-sm border border-surface-200 rounded-xl px-3 py-2
                           text-ink-600 bg-white focus:outline-none focus:ring-2 focus:ring-brand-200">
                <option value="">Select a reason (optional)</option>
                {CANCEL_REASONS.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
              <div className="flex gap-2">
                <button
                  onClick={handleCancel}
                  disabled={cancelling}
                  className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl
                             bg-red-500 text-white text-sm font-medium hover:bg-red-600
                             transition-colors disabled:opacity-60">
                  {cancelling
                    ? <><Loader2 size={14} className="animate-spin" /> Cancelling…</>
                    : "Yes, cancel"}
                </button>
                <button
                  onClick={() => { setConfirming(false); setReason(""); }}
                  className="flex-1 py-2.5 rounded-xl border border-surface-200 text-ink-600
                             text-sm hover:bg-surface-50 transition-colors">
                  Keep it
                </button>
              </div>
            </div>
          )}

          {/* Action buttons */}
          {!confirming && (
            <div className="space-y-3">
              {canManage && (
                <>
                  <button
                    onClick={() => setRescheduleOpen(true)}
                    className="btn-primary w-full flex items-center justify-center gap-2">
                    <CalendarCheck size={15} /> Reschedule
                  </button>
                  <button
                    onClick={() => setConfirming(true)}
                    className="w-full py-2.5 rounded-xl border border-red-200 text-red-500
                               hover:bg-red-50 text-sm font-medium transition-all flex items-center
                               justify-center gap-2">
                    <X size={14} /> Cancel appointment
                  </button>
                </>
              )}

              {tooLate && isUpcoming && (
                <div className="card p-4 text-center text-sm text-amber-600
                                bg-amber-50 border border-amber-200 rounded-xl">
                  Changes require {cancelPolicyHours}h notice. Please contact {tenant?.name || "the shop"} directly.
                </div>
              )}

              {!user && (
                <button
                  onClick={() => navigate("/signup")}
                  className="w-full py-2.5 rounded-xl border border-brand-200 text-brand-600
                             hover:bg-brand-50 text-sm font-medium transition-all flex items-center
                             justify-center gap-2">
                  <UserPlus size={14} /> Create account to manage all bookings
                </button>
              )}

              <button
                onClick={() => navigate(`/${slug}/book`)}
                className="btn-secondary w-full text-sm">
                Book another appointment
              </button>
            </div>
          )}
        </div>
      </div>

      {rescheduleOpen && (
        <RescheduleModal
          booking={booking}
          onClose={() => setRescheduleOpen(false)}
          onSuccess={handleRescheduleSuccess}
        />
      )}
    </>
  );
}
